import * as React from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { useUpdateVendorPublicPage, useVendorPublicPage } from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Callout } from '@/components/ui/callout'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ErrorState, LoadingState } from '@/components/ui/states'
import { FormError } from '@/components/ui/form-error'
import type { VendorPageHidden, VendorPublicPageSettingsDto } from '@lib/dto/vendor-public-page.dto'

/** Why a page that is on isn't showing, said plainly. */
const HIDDEN_REASON: Readonly<Record<VendorPageHidden, string>> = {
  incomplete: 'Your page is on, but it isn’t showing: your profile is missing something above. It comes back by itself once that’s done.',
  unconfirmed: 'Your page is on, but it isn’t showing until you confirm your email.',
}

/** What stops the box being ticked, when something does. */
function blockedNote(settings: VendorPublicPageSettingsDto): string | null {
  if (!settings.canPublish) return 'Only an owner can publish or unpublish your page.'
  if (!settings.on && settings.missing.length > 0) return 'Finish the checklist above to publish.'
  return null
}

/** The page's address, with a way to copy it for the company's own site and social accounts. */
function PageLink({ url }: { url: string }) {
  const [copied, setCopied] = React.useState(false)

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 2000)
    } catch {
      // Refused (no permission, an insecure page). The address is on screen and
      // can be selected, so there is still a way through.
      setCopied(false)
    }
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-line bg-surface-sunken px-3 py-2 font-mono text-sm">
          {url}
        </code>
        <Button variant="outline" onClick={() => void copy()} className="gap-2">
          {copied ? <Check className="h-4 w-4" aria-hidden /> : <Copy className="h-4 w-4" aria-hidden />}
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-accent-ink underline underline-offset-2"
      >
        See your page <ExternalLink className="h-3.5 w-3.5" aria-hidden />
      </a>
    </div>
  )
}

/**
 * The company's page on csvenueview.com (docs/vendor-growth.spec.md, "The public
 * page"), on the Profile page under the checklist. Off until an owner publishes
 * it, which needs the checklist done; everyone at the company sees where it
 * stands.
 */
export function PublicPageCard() {
  const page = useVendorPublicPage()
  const update = useUpdateVendorPublicPage()
  const [error, setError] = React.useState<string | null>(null)

  function publish(on: boolean): void {
    setError(null)
    update.mutate({ on }, { onError: (cause) => setError(requestErrorMessage(cause)) })
  }

  function body(settings: VendorPublicPageSettingsDto): React.ReactNode {
    const blocked = blockedNote(settings)
    const showing = settings.on && settings.hidden === null
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <label className="inline-flex items-center gap-2 text-sm font-medium text-ink">
            <Checkbox
              checked={settings.on}
              disabled={!settings.canPublish || update.isPending || (!settings.on && settings.missing.length > 0)}
              onCheckedChange={(on) => publish(on === true)}
            />
            Show your page on csvenueview.com
          </label>
          {blocked === null ? null : <p className="text-sm text-ink-muted">{blocked}</p>}
          {error === null ? null : <FormError>{error}</FormError>}
        </div>
        {settings.on && settings.hidden !== null ? <Callout tone="attention">{HIDDEN_REASON[settings.hidden]}</Callout> : null}
        {/* The site shares the app's domain, so the page is at this origin. */}
        {showing && settings.path !== null ? <PageLink url={`${window.location.origin}${settings.path}`} /> : null}
      </div>
    )
  }

  return (
    <Card id="public-page">
      <CardHeader>
        <CardTitle>Your page on csvenueview.com</CardTitle>
        <CardDescription>
          Anyone can find it, search engines included. It shows your profile, your packages with their prices as you chose, your contact details,
          who recommends you, and how many events you’ve worked on FLOH Venues, without naming where.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {page.isPending ? <LoadingState label="Loading your page…" /> : null}
        {page.isError ? <ErrorState message={requestErrorMessage(page.error)} onRetry={() => void page.refetch()} /> : null}
        {page.data ? body(page.data) : null}
      </CardContent>
    </Card>
  )
}
